// BL-889 — THE definition of "is this inventory item valid?".
//
// Before this file, the answer was written down three times and the copies
// disagreed: the mobile Add Item form checked four fields, the admin lot-edit
// routes checked seven (with their own wording), and the mobile sync route
// checked none — so a 6-character VIN was refused on the phone, accepted by the
// sync two minutes later, and finally rejected by a Postgres CHECK as an opaque
// 500. The rules now live here once. (The mobile app and its sync route went with
// BL-1444; BL-2339 deleted the snake_case sync schema that outlived them.)
//
// SCOPE — "today's rules only" (BL-889 decision). This file codifies exactly
// what production enforced before it existed. The orphaned
// `apps/admin-web/src/lib/schemas/inventory.ts` this replaces additionally
// declared estimate-high >= estimate-low, status/title-status enums and a
// required tenantId — but nothing consumed it, so nothing enforced them.
// Adopting them here would 400 currently-valid traffic (AC 4). Adding a rule is
// a product decision that needs its own backlog item.
//
// ISOMORPHIC (sharing.md § 3). The admin item form imports this module in the
// browser, so it stays free of server code: never add `@auctogon/db`, `next/*`,
// `server-only`, or `node:*`. (It used to be held to "pure Zod" for the Expo
// catalog app's Metro bundle; BL-1444 deleted that app, and BL-2131 lets
// `./item-form` import the shared lot-money field.) The `TITLE_STATUSES` /
// `inventory_title_status` parity this costs us is bought back by
// `__tests__/title-status-parity.test.ts`, which is test-only and therefore free
// to import both sides.

import { z } from '@auctogon/core/zod';
import { TITLE_STATUSES, TITLE_MAILING_STATUSES } from '../isomorphic/item-form';
// NOTE — the `title` rule is NOT redefined here. It already lives in
// `./item-form` as `RequiredTitleSchema` (BL-830) and is shared by the client
// form, the create route and the lot PATCH; this module composes it rather than
// restating it. It is deliberately NOT re-exported either: the `./schemas`
// barrel re-exports both files, so a re-export would collide on the same name.
// Import direction is one-way (inventory → item-form); never the reverse.
import { RequiredTitleSchema } from './item-form';
// Local bindings for the re-exported message constants below — `export … from`
// re-exports without binding them in this module's scope, and the atoms here
// reference them directly.
import {
  INVENTORY_VIN_MESSAGE,
  INVENTORY_YEAR_MESSAGE,
  INVENTORY_HOURS_MILEAGE_MESSAGE,
} from './inventory-messages';

// ---------------------------------------------------------------------------
// Messages — one string per rule, in one place
// ---------------------------------------------------------------------------
//
// These are the operator-visible copy. They carry NO trailing period, matching
// the client-facing variants that already render inline under the fields in
// `ItemForm` (and the sibling `TITLE_REQUIRED_MESSAGE = 'Title is required'`).
//
// BL-889 note: the admin lot routes previously used period-terminated twins
// ('VIN must be exactly 17 characters.') while the create route and the React
// form used these. Same rule, two wordings — precisely the drift AC 2 forbids.
// The no-period variant wins because it is the one operators actually see.

// Defined in the leaf `./inventory-messages` and re-exported here so this
// module stays the one canonical import path for consumers. They can't live in
// this file: `POST /api/inventory`'s `InventoryCreatePayloadSchema` (in
// `./item-form`) needs them too, and this file already imports from
// `./item-form` — so owning them here would force a cycle whose temporal dead
// zone would silently yield `undefined` messages. With the leaf, all four
// inventory surfaces genuinely share one copy of each string rather than
// coincidentally matching literals (BL-889 AC 2).
export {
  INVENTORY_VIN_MESSAGE,
  INVENTORY_YEAR_MESSAGE,
  INVENTORY_HOURS_MILEAGE_MESSAGE,
} from './inventory-messages';

/** `inventory_title_status` is a native Postgres ENUM — an unknown value raises 22P02 (BUG-1041). */
export const INVENTORY_TITLE_STATUS_MESSAGE = 'Title status must be a recognized value';

/** Same ENUM hazard as title status — `inventory_title_mailing_status` (BUG-1091). */
export const INVENTORY_TITLE_MAILING_STATUS_MESSAGE =
  'Title mailing status must be a recognized value';

/** `title_mailing_date_sent` is `timestamptz` — an unparseable value raises 22007. */
export const INVENTORY_TITLE_MAILING_DATE_SENT_MESSAGE = 'Date sent must be a valid date';

/** Exact VIN width. */
export const INVENTORY_VIN_LENGTH = 17;
/** Shared max width for the free-text equipment identifiers. */
export const INVENTORY_MAX_TEXT_LENGTH = 100;
/** `auction_lots.title_mailing_recipient_name` is `varchar(255)` (BUG-1091). */
export const INVENTORY_RECIPIENT_NAME_MAX_LENGTH = 255;
/**
 * `auction_lots.title_mailing_recipient_address` is unbounded `TEXT`. Capped
 * generously rather than at the shared 100 so a real multiline
 * `street\ncity, ST zip` never trips a limit the column itself doesn't have.
 */
export const INVENTORY_RECIPIENT_ADDRESS_MAX_LENGTH = 500;
/** Inclusive year bounds. */
export const INVENTORY_MIN_YEAR = 1900;
export const INVENTORY_MAX_YEAR = 2100;

/**
 * Message for an over-long free-text equipment field.
 *
 * @param label Human label for the field ("Make", "Serial number") — leads the sentence.
 */
export const inventoryMaxLengthMessage = (
  label: string,
  maxLength: number = INVENTORY_MAX_TEXT_LENGTH,
): string => `${label} must be ${maxLength} characters or fewer`;

// ---------------------------------------------------------------------------
// Field atoms
// ---------------------------------------------------------------------------
//
// PERMISSIVENESS IS DELIBERATE, NOT SLOPPINESS. Each atom is built on
// `z.unknown()` + `superRefine` rather than `z.string()` / `z.coerce.number()`
// because it must reproduce the exact tolerance of the hand-rolled guards it
// replaces (`validateItemOwnedFields`), where:
//
//   * a NON-STRING value is treated as absent, not as a type error;
//   * `''` means "not supplied" — but `'   '` does NOT (a whitespace VIN is
//     written verbatim downstream, so it must be length-checked);
//   * a NON-FINITE numeric (`'abc'`) is skipped rather than rejected;
//   * a numeric STRING (`'2020'`) is accepted.
//
// A `z.coerce.number()` rewrite fails three of those silently — `Number('')`
// and `Number(null)` are both `0`, which would then fail the year range and
// start 400ing saves that work today (AC 4). Those behaviors are pinned by
// `__tests__/inventory.test.ts` → "permissiveness on garbage input".
//
// Tightening any of this is a product decision with its own backlog item. The
// atoms are a GATE, not a transform: callers read fields off the original body,
// which is why every object schema below is `.passthrough()`.

/** True when a value is a supplied string (i.e. present and not the empty string). */
const isSuppliedString = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

/**
 * Builds an atom for an optional free-text field with a maximum width.
 *
 * @param label Human label used in the error message.
 */
const optionalBoundedText = (label: string, maxLength: number = INVENTORY_MAX_TEXT_LENGTH) =>
  z.unknown().superRefine((value, ctx) => {
    if (!isSuppliedString(value)) return;
    if (value.length > maxLength) {
      ctx.addIssue({ code: 'custom', message: inventoryMaxLengthMessage(label, maxLength) });
    }
  });

/**
 * Builds an atom for an optional numeric field.
 *
 * @param parse   How to turn a supplied value into a number — mirrors the
 *                parser the replaced guard used (`parseInt` for year,
 *                `parseFloat` for hours/mileage), so rounding edges don't shift.
 * @param isValid Predicate a finite parsed value must satisfy.
 * @param message Error message when `isValid` fails.
 */
const optionalNumeric = (
  parse: (raw: string) => number,
  isValid: (value: number) => boolean,
  message: string,
) =>
  z.unknown().superRefine((value, ctx) => {
    if (value === undefined || value === null || value === '') return;
    const parsed = typeof value === 'number' ? value : parse(String(value));
    // Non-finite input is treated as absent, matching the guards this replaces.
    if (!Number.isFinite(parsed)) return;
    if (!isValid(parsed)) ctx.addIssue({ code: 'custom', message });
  });

/** VIN: exactly 17 characters when supplied. `''` is "not supplied"; `'   '` is NOT. */
export const InventoryVinSchema = z.unknown().superRefine((value, ctx) => {
  if (!isSuppliedString(value)) return;
  if (value.length !== INVENTORY_VIN_LENGTH) {
    ctx.addIssue({ code: 'custom', message: INVENTORY_VIN_MESSAGE });
  }
});

/** Model year, inclusive of both bounds. Accepts a numeric string. */
export const InventoryYearSchema = optionalNumeric(
  (raw) => parseInt(raw, 10),
  (year) => year >= INVENTORY_MIN_YEAR && year <= INVENTORY_MAX_YEAR,
  INVENTORY_YEAR_MESSAGE,
);

/** Hours or mileage. Fractional readings are legitimate — never add `.int()`. */
export const InventoryHoursMileageSchema = optionalNumeric(
  (raw) => parseFloat(raw),
  (hours) => hours >= 0,
  INVENTORY_HOURS_MILEAGE_MESSAGE,
);

/** Title status: one of the `inventory_title_status` enum values, or absent. */
export const InventoryTitleStatusSchema = z.unknown().superRefine((value, ctx) => {
  if (!isSuppliedString(value)) return;
  if (!(TITLE_STATUSES as readonly string[]).includes(value)) {
    ctx.addIssue({ code: 'custom', message: INVENTORY_TITLE_STATUS_MESSAGE });
  }
});

/**
 * Title mailing status: one of the `inventory_title_mailing_status` enum
 * values, or absent (BUG-1091). Mirrors `InventoryTitleStatusSchema` — an
 * unrecognized value must fail here as an inline 400, never reach the column
 * and raise a 22P02 500.
 */
export const InventoryTitleMailingStatusSchema = z.unknown().superRefine((value, ctx) => {
  if (!isSuppliedString(value)) return;
  if (!(TITLE_MAILING_STATUSES as readonly string[]).includes(value)) {
    ctx.addIssue({ code: 'custom', message: INVENTORY_TITLE_MAILING_STATUS_MESSAGE });
  }
});

/** A bare calendar day — the shape `DatePicker` emits. */
const LOGICAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Title mailing date sent (BUG-1091). Accepts BOTH shapes that legitimately
 * reach this route:
 *
 *   1. `YYYY-MM-DD` — what `DatePicker` emits when an operator picks a date.
 *   2. A full timestamp string — the untouched round-trip of the stored
 *      `timestamptz` when the operator saves without touching the date. The
 *      write path passes that through byte-identical on purpose (see
 *      `toUtcMidnightInstant`), so an unrelated save never shifts the stored
 *      instant. Rejecting it here would break saving any lot that already has
 *      a date set.
 *
 * Anything else is an inline 400 rather than a 22007 at the column.
 *
 * The logical-date branch returns BEFORE the `Date.parse` fallback, so a bare
 * calendar day is never put through an instant parse — this is a validity
 * check on an already-instant-shaped string, not `logical-dates.md`'s banned
 * logical-date→instant round-trip.
 */
export const InventoryTitleMailingDateSentSchema = z.unknown().superRefine((value, ctx) => {
  if (!isSuppliedString(value)) return;
  if (LOGICAL_DATE_PATTERN.test(value)) return;
  if (!Number.isNaN(Date.parse(value))) return;
  ctx.addIssue({ code: 'custom', message: INVENTORY_TITLE_MAILING_DATE_SENT_MESSAGE });
});

export const InventoryMakeSchema = optionalBoundedText('Make');
export const InventoryModelSchema = optionalBoundedText('Model');
export const InventorySerialNumberSchema = optionalBoundedText('Serial number');
/** Title mailing free-text — each width matches its column on `auction_lots`. */
export const InventoryTitleMailingTrackingNumberSchema = optionalBoundedText('Tracking number');
export const InventoryTitleMailingRecipientNameSchema = optionalBoundedText(
  'Recipient name',
  INVENTORY_RECIPIENT_NAME_MAX_LENGTH,
);
export const InventoryTitleMailingRecipientAddressSchema = optionalBoundedText(
  'Recipient address',
  INVENTORY_RECIPIENT_ADDRESS_MAX_LENGTH,
);

// ---------------------------------------------------------------------------
// Object schemas — one per wire shape
// ---------------------------------------------------------------------------
//
// The ATOMS above are shared and each object schema below binds them to its own
// keys. (A snake_case binding for the retired mobile sync payload lived here
// until BL-2339 deleted it with the last trace of that route.)

/**
 * The item-owned catalog fields as the admin web routes receive them.
 * Consumed by `validateItemOwnedFields` (lot PATCH/PUT).
 */
export const InventoryItemFieldsSchema = z
  .object({
    vin: InventoryVinSchema,
    make: InventoryMakeSchema,
    model: InventoryModelSchema,
    serialNumber: InventorySerialNumberSchema,
    year: InventoryYearSchema,
    hoursMileage: InventoryHoursMileageSchema,
    titleStatus: InventoryTitleStatusSchema,
  })
  .partial()
  .passthrough();

export type InventoryItemFieldsInput = z.infer<typeof InventoryItemFieldsSchema>;

/**
 * Body schema for `PATCH /api/lots/[id]` — the catalog-edit write path since
 * BL-950 inverted catalog onto `auction_lots` and retired the item PUT.
 *
 * The item-owned fields plus the lot's listing `name`. `name` is
 * optional-but-non-blank: a partial patch (a make-only edit) omits it and must
 * keep working, but `name: ''` must not blank out a real listing name
 * (BUG-975). Ordered name-first so the `fieldErrors` map reads the way the
 * form does.
 *
 * Having this as ONE schema is what lets the route return the standard
 * `parseBody` envelope instead of hand-rolling a third error shape.
 *
 * BL-1059: this used to validate a `title` key that no live client sent (the
 * shared form/mapper chain already emits `name` for this route) — the
 * BUG-975 non-blank guard was a silent no-op. Renamed to match the field the
 * route actually writes (`buildLotPatch` reads `body.name`).
 */
export const LotCatalogPatchSchema = z
  .object({
    name: RequiredTitleSchema.optional(),
    vin: InventoryVinSchema,
    make: InventoryMakeSchema,
    model: InventoryModelSchema,
    serialNumber: InventorySerialNumberSchema,
    year: InventoryYearSchema,
    hoursMileage: InventoryHoursMileageSchema,
    titleStatus: InventoryTitleStatusSchema,
    // Title mailing (BUG-1091). These live on `auction_lots` (inverted by
    // BL-939, dropped from `inventory_items` by BL-950), so the lot PATCH is
    // their only write path. Validated here so an unrecognized enum value or an
    // over-long string is an inline 400, never a 22P02/22001 500 at the column.
    titleMailingStatus: InventoryTitleMailingStatusSchema,
    titleMailingTrackingNumber: InventoryTitleMailingTrackingNumberSchema,
    titleMailingDateSent: InventoryTitleMailingDateSentSchema,
    titleMailingRecipientName: InventoryTitleMailingRecipientNameSchema,
    titleMailingRecipientAddress: InventoryTitleMailingRecipientAddressSchema,
    // BL-1231 — lot custom-field VALUES, keyed by slot string. Shape-only guard
    // here (values are text); the config-aware validation (checkbox ∈ true/false,
    // picklist ∈ options, slot enabled) runs in the route via
    // `validateLotCustomFieldValues` since it depends on the tenant's config.
    customFields: z.record(z.string(), z.string()).optional(),
    // BL-2694 — `buildLotPatch` writes this straight to a boolean column with no
    // coercion, so an unvalidated non-boolean here would 500 at the column
    // instead of 400 at the route.
    visibleToSeller: z.boolean().optional(),
  })
  .partial()
  .passthrough();

export type LotCatalogPatchInput = z.infer<typeof LotCatalogPatchSchema>;

/**
 * Machine-readable codes for the enforced rules, surfaced through
 * `SchemaManifest.inventory.errorCodes` so an agent can enumerate the failure
 * modes of this domain without reading the schema.
 */
export const InventoryErrorCodes = {
  TITLE_REQUIRED: 'inventory.title_required',
  YEAR_OUT_OF_RANGE: 'inventory.year_out_of_range',
  VIN_INVALID_LENGTH: 'inventory.vin_invalid_length',
  TEXT_TOO_LONG: 'inventory.text_too_long',
  NEGATIVE_AMOUNT: 'inventory.negative_amount',
  INVALID_TITLE_STATUS: 'inventory.invalid_title_status',
  INVALID_TITLE_MAILING_STATUS: 'inventory.invalid_title_mailing_status',
  INVALID_TITLE_MAILING_DATE_SENT: 'inventory.invalid_title_mailing_date_sent',
} as const;

export type InventoryErrorCode = (typeof InventoryErrorCodes)[keyof typeof InventoryErrorCodes];
